import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  authForgot,
  authLogin,
  authLogout,
  authMe,
  authRegister,
  authReset,
  formatApiErrorDetail,
} from "@/lib/api";

const AuthContext = createContext(null);

// user states:
//   undefined -> checking existing session
//   null      -> not authenticated
//   object    -> authenticated user { id, email, name, role, language }
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authMe();
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await authLogin({ email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await authRegister(payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authLogout();
    } catch {
      // ignore - we still clear the local state
    }
    setUser(null);
  }, []);

  const forgot = useCallback(async (email, language) => {
    try {
      await authForgot({ email, language });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const reset = useCallback(async (token, password) => {
    try {
      await authReset({ token, password });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, forgot, reset, refresh }),
    [user, login, register, logout, forgot, reset, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
